import { PartialBy } from '@upright/common/types';
import { AndAssessmentPriorization, IBaseCompanyAssessment, MetricNature } from '../types';

// Note: keep in sync with API PaiFieldKey
export enum PaiMetric {
  GHG1 = 'GHG1',
  GHG2 = 'GHG2',
  GHG3 = 'GHG3',
  GHGF = 'GHGF',
  GHGF12 = 'GHGF12',
  GHGI = 'GHGI',
  GHGI12 = 'GHGI12',
  AIFF = 'AIFF',
  NRNW = 'NRNW',
  NRGI = 'NRGI',
  BIOD = 'BIOD',
  WTRE = 'WTRE',
  HAZW = 'HAZW',
  SOCV = 'SOCV',
  SOCP = 'SOCP',
  PAYG = 'PAYG',
  FMBR = 'FMBR',
  CVWP = 'CVWP',
  HICS = 'HICS',
  TECO = 'TECO',
  CHEM = 'CHEM',
  HRSP = 'HRSP',
  ACBP = 'ACBP', // anti-corruption/anti-bribery
  WTRU = 'WTRU',
  // WTRR = 'WTRR',
}
export const wipPaiMetrics: Set<PaiMetric> = new Set();

export type NumericPaiMetric =
  | PaiMetric.GHG1
  | PaiMetric.GHG2
  | PaiMetric.GHG3
  | PaiMetric.GHGF
  | PaiMetric.GHGF12
  | PaiMetric.GHGI
  | PaiMetric.GHGI12
  | PaiMetric.NRNW
  | PaiMetric.NRGI
  | PaiMetric.WTRE
  | PaiMetric.HAZW
  | PaiMetric.PAYG
  | PaiMetric.FMBR
  | PaiMetric.TECO
  | PaiMetric.WTRU;
// | PaiMetric.WTRR;

export type PositivePaiMetric =
  | PaiMetric.AIFF
  | PaiMetric.BIOD
  | PaiMetric.CHEM
  | PaiMetric.SOCV
  | PaiMetric.SOCP
  | PaiMetric.CVWP;

export type EnumPaiMetric = PaiMetric.HICS | PaiMetric.HRSP | PaiMetric.ACBP;

/**
 * This ought to be the only place where you need to maintain both the type and list for numeric and positive metrics...
 */
export const numericPaiMetrics = new Set([
  PaiMetric.GHG1,
  PaiMetric.GHG2,
  PaiMetric.GHGF12,
  PaiMetric.GHGI12,
  PaiMetric.GHG3,
  PaiMetric.GHGF,
  PaiMetric.GHGI,
  PaiMetric.NRNW,
  PaiMetric.NRGI,
  PaiMetric.WTRE,
  PaiMetric.HAZW,
  PaiMetric.PAYG,
  PaiMetric.FMBR,
  PaiMetric.TECO,
  PaiMetric.WTRU,
  // PaiMetric.WTRR,
]);
export const possiblyNegativePaiMetrics = new Set([PaiMetric.PAYG]);
export const binaryPaiMetrics = new Set([
  PaiMetric.AIFF,
  PaiMetric.BIOD,
  PaiMetric.CHEM,
  PaiMetric.SOCV,
  PaiMetric.SOCP,
  PaiMetric.CVWP,
]);
export const enumPaiMetrics = new Set([PaiMetric.HICS, PaiMetric.ACBP, PaiMetric.HRSP]);
export const allPaiMetrics = new Set([
  PaiMetric.GHG1,
  PaiMetric.GHG2,
  PaiMetric.GHG3,
  PaiMetric.GHGF12,
  PaiMetric.GHGF,
  PaiMetric.GHGI12,
  PaiMetric.GHGI,
  PaiMetric.AIFF,
  PaiMetric.NRNW,
  PaiMetric.HICS,
  PaiMetric.TECO,
  PaiMetric.NRGI,
  PaiMetric.BIOD,
  PaiMetric.WTRE,
  PaiMetric.HAZW,
  PaiMetric.CHEM,
  PaiMetric.SOCV,
  PaiMetric.SOCP,
  PaiMetric.PAYG,
  PaiMetric.FMBR,
  PaiMetric.CVWP,
  PaiMetric.HRSP,
  PaiMetric.ACBP,
  PaiMetric.WTRU,
  // PaiMetric.WTRR,
]);

export function isPaiMetric(value: any): value is PaiMetric {
  return allPaiMetrics.has(value);
}

/**
 * Absolute PAI metrics that are e.g,. not generalizable from a parent company to its subsidiaries
 */
export const absolutePaiMetrics = new Set([
  PaiMetric.GHG1,
  PaiMetric.GHG2,
  PaiMetric.GHG3,
  PaiMetric.GHGF12,
  PaiMetric.GHGF,
  PaiMetric.TECO,
  PaiMetric.WTRE,
  PaiMetric.HAZW,
]);

/**
 * PAI metrics that are likely derived from another PAI metrics and therefore can contradict the "base" metric
 */
export const derivedPaiMetrics = new Set([
  PaiMetric.GHGF,
  PaiMetric.GHGF12,
  PaiMetric.GHGI12,
  PaiMetric.GHGF,
  PaiMetric.GHGI,
  PaiMetric.WTRE,
  PaiMetric.NRGI,
]);

/**
 * Caution adviced: numeric enums allow random numbers to be assigned without type errors, so
 * we just need to be careful not to assign e.g., 150 to a PositiveIndicatorValue...
 * https://github.com/microsoft/TypeScript/issues/26362
 *
 * Why numeric enums? Can use the same numeric field in DB and elsewhere for actual numeric values
 * as well as these enum values
 */
export enum PositiveIndicatorValue {
  YES = 1,
  NO = 0, // Evidence found for HRSP and ACBP, no evidence found for other positive pai indicators
}

export enum PolicyStatus {
  STATED_POLICY = 2,
  MENTION_ONLY = 1,
  NO_MENTIONS = 0,
}

/**
 * Eero's excuse to integer encode the alphabet: we want to store these in the numeric value column of pai_assessment
 */
export enum NaceCode {
  A = 1,
  B = 2,
  C = 3,
  D = 4,
  E = 5,
  F = 6,
  G = 7,
  H = 8,
  I = 9,
  J = 10,
  K = 11,
  L = 12,
  M = 13,
  N = 14,
  O = 15,
  P = 16,
  Q = 17,
  R = 18,
  S = 19,
  T = 20,
  U = 21,
  NONE = 999,
}

export interface BasePaiAssessment {
  id: number;
  companyId: string;
  metric: PaiMetric;
  nature: MetricNature.DIRECT_DISCLOSURE | MetricNature.THIRD_PARTY_ASSESSMENT | MetricNature.UPRIGHT_MANUAL_ASSESSMENT;
  isInactive: boolean;
  year: number;
  source?: string;
  sourceUrl?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: number;
  createdByUserName?: string;
  trace?: string;
  rationale?: string;
  inUse?: boolean;
  readableBy: number;
}
export interface NumericPaiAssessment extends BasePaiAssessment {
  metric: NumericPaiMetric;
  value: number;
}

export interface PositivePaiAssessment extends BasePaiAssessment {
  metric: PositivePaiMetric;
  value: PositiveIndicatorValue;
}

interface NacePaiAssessment extends BasePaiAssessment {
  metric: PaiMetric.HICS;
  value: NaceCode;
}

interface HasPolicyAssessment extends BasePaiAssessment {
  metric: PaiMetric.ACBP | PaiMetric.HRSP;
  value: PolicyStatus;
}

export type EnumPaiAssessment = NacePaiAssessment | HasPolicyAssessment;

export type PaiAssessment = NumericPaiAssessment | PositivePaiAssessment | EnumPaiAssessment;

export type PaiAssessmentWithPrioritization = PaiAssessment & AndAssessmentPriorization;

export interface IPaiAssessment extends IBaseCompanyAssessment {
  metric: PaiMetric;
  value: number;
}

export type CreatePaiAssessmentPayload = PartialBy<Omit<IPaiAssessment, 'id'>, 'createdBy'>;
export type CreatePaiAssessmentsPayload = CreatePaiAssessmentPayload[];

export interface UpdatePaiAssessmentPayload {
  companyId?: string;
  nature?: MetricNature;
  year?: number;
  value?: number;
  source?: string;
  sourceUrl?: string;
  rationale?: string;
  isInactive?: boolean;
}

export interface UpdatePaiAssessmentAccessPayload {
  readableBy: number;
}
